<template>
  <tr>
    <td align="left" :style="rootStyles">
      <div v-if="!isBuilder" :style="{ textAlign: contentModel.style.align }">
        <a v-if="contentModel.href" :href="contentModel.href">
          <img
            :alt="contentModel.alt"
            :src="optimisedImage"
            :style="{ width: contentModel.style.size + '%' }"
          />
        </a>
        <img
          v-else
          :alt="contentModel.alt"
          :src="optimisedImage"
          :style="{ width: contentModel.style.size + '%' }"
        />
      </div>
      <template v-else>
        <ak-overlay>
          <div :style="{ textAlign: contentModel.style.align }">
            <img
              :alt="contentModel.alt"
              :src="optimisedImage"
              :style="{ width: contentModel.style.size + '%' }"
            />
          </div>
          <template #overlayBody>
            <div class="text-center">
              <ak-button class="m-tiny" size="small" @click="openMediaManager"
                >Choose Image</ak-button
              >
              <ak-popover
                theme="dark"
                width="300px"
                size="normal"
                class="m-tiny"
              >
                <template #trigger>
                  <ak-button size="small" color="transparent-light"
                    >Settings</ak-button
                  >
                </template>
                <template #popover>
                  <ak-field label="Size" has-dark-bg class="mb-small">
                    <ak-range
                      v-model:value="contentModel.style.size"
                      :min="10"
                      :max="100"
                      :step="5"
                      :show-bubbles="false"
                    />
                  </ak-field>
                  <ak-field label="Alignment" has-dark-bg class="mb-small">
                    <ak-radio-group
                      v-model="contentModel.style.align"
                      :options="alignOpts"
                      name="buttonAlignment"
                    >
                    </ak-radio-group>
                  </ak-field>
                  <ak-field label="Link" has-dark-bg class="mb-none">
                    <ak-input
                      v-model="contentModel.href"
                      type="url"
                      validation-message="Must be a valid url"
                    />
                  </ak-field>
                </template>
              </ak-popover>
            </div>
          </template>
        </ak-overlay>
      </template>
    </td>
  </tr>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import { MediaManagerProgrammatic } from '@akerolabs/akero-vue/src/components/mediaManager'
import config from '../../utils/config'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-email-template-image></ak-email-template-image>
   */
  name: 'AkEmailTemplateImage',

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description The content to render/edit
     */
    modelValue: {
      type: Object,
      default: () => ({
        src: config.defaultImage,
        type: 'image',
        style: config.defaultImageOptions,
      }),
    },

    /**
     * @type {Number}
     * @description The width of the column
     */
    width: {
      type: Number,
      default: null,
    },

    /**
     * @type {Object}
     * @description The settings for the template
     */
    settings: {
      type: Object,
      default: () => ({ ...config.defaultTemplateStyles }),
    },

    /**
     * @type {Boolean}
     * @description Determines if we are to render the block for the
     * builder component (drag and drop editor)
     */
    isBuilder: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Number}
     * @description Block index
     */
    blockIndex: {
      type: Number,
      default: 0,
    },

    /**
     * @type {Number}
     * @description Content index
     */
    contentIndex: {
      type: Number,
      default: 0,
    },

    /**
     * @type {Boolean}
     * @description Determines if the content item is the last in its column
     */
    isLast: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Has flush spacing on the block
     */
    isFlush: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description The container in which to position the drawers
     */
    drawerContainer: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const contentModel = computed({
      get() {
        return props.modelValue
      },
      set(val) {
        emit('update:modelValue', val)
      },
    })

    const alignOpts = ref(config.alignmentOptions)

    /**
     * @returns {Object}
     * @description returns the root styles for the component
     */
    const rootStyles = computed(() => {
      return {
        fontSize: !props.isBuilder ? '0px' : null,
        padding: '0',
        wordBreak: 'break-word',
        paddingTop:
          !props.isBuilder && props.contentIndex === 0 && !props.isFlush
            ? '10px'
            : '0',
        paddingBottom: props.isBuilder || props.isLast ? '0' : '10px',
      }
    })

    const optimisedImage = computed(() => {
      return contentModel.value.src.replace(
        '/upload/',
        `/upload/c_fill,q_auto,dpr_auto,f_auto,w_${props.width}/`,
      )
    })

    const openMediaManager = () => {
      MediaManagerProgrammatic.open({
        callback: (response) => {
          contentModel.value.src = response.items[0].source
          contentModel.value.alt = response.details.alt
          contentModel.value.href = response.items[0].link
        },
      })
    }

    return {
      alignOpts,
      rootStyles,
      optimisedImage,
      openMediaManager,
      contentModel,
    }
  },
})
</script>
